import { BaseWebComponentDataProvider } from '../../../framework/BaseWebComponentDataProvider';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import {
  Action,
  Column,
  ColumnKeys,
  CustomRuleDetails,
  ResponseGridViewData,
  Source,
  Status,
  Target,
  Type,
} from '../types';
import { ColumnDetails, ColumnType, ColumnValue } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { CategoriesConfig } from '../../../grid/genericGridSmartViews/types';
import { EnumUtils } from '../../../utils/enum.utils';
import { SelectOption } from '../../../ui/gzSelect/types';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';

export class ResponseGridViewDataProvider extends BaseWebComponentDataProvider {
  private stringsTranslator = BaseWebComponentTranslator.getInstance();

  public getDataForView(incidentId: string): ResponseGridViewData {
    return {
      incidentId,
      smartViewsCategories: this.getSmartViewsCategories(),
      allColumns: this.getAllColumns(),
      predefinedFilterKeys: [
        ColumnKeys.ACTION,
        ColumnKeys.OBJECT,
        ColumnKeys.TARGET,
        ColumnKeys.STATUS,
      ],
      allUpdateStatusActions: this.getAllUpdateStatusActions(),
    };
  }

  public async updateStatus(id: string, status: Status): Promise<void> {
    await ConsoleRequest.createRequest({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'EdrResponseActions',
      method: 'updateStatusAsUser',
      payload: { id, status },
    });
  }

  public async getCustomRuleDetails(id: string): Promise<CustomRuleDetails> {
    return (await ConsoleRequest.createRequest<{ data: CustomRuleDetails }>(
      { dataProviderType: DataProviderType.CORE, endpoint: 'DetectionRules', method: 'getDetails', payload: { id } }
    )).data;
  }

  private getSmartViewsCategories(): CategoriesConfig {
    return {
      customCategories: [],
      internalCategories: {
        recent: {
          isAvailable: false,
        },
        default: {
          isAvailable: true,
          translations: {
            action_needed: translateFn('Action needed'),
            in_progress: translateFn('In progress'),
            completed: translateFn('Completed'),
            dismissed: translateFn('Dismissed'),
            all_actions: translateFn('All actions'),
          },
        },
        favorites: {
          isAvailable: false,
        },
      },
    };
  }

  private getAllColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.TYPE,
        key: ColumnKeys.TYPE,
        columnName: translateFn('Type'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<Type>(Type).map((type: Type): ColumnValue => ({
          label: this.stringsTranslator.translateEdrResponseActionsType(type),
          value: type,
        })),
        removable: false,
        filterResetValue: [],
        sortable: true,
      },
      {
        column: Column.ACTION,
        key: ColumnKeys.ACTION,
        columnName: translateFn('Action'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<Action>(Action).map((action: Action): ColumnValue => ({
          label: this.stringsTranslator.translateEdrResponseActionsAction(action),
          value: action,
        })),
        removable: false,
        filterResetValue: [],
        sortable: true,
      },
      {
        column: Column.OBJECT,
        key: ColumnKeys.OBJECT,
        columnName: translateFn('Object'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
      },
      {
        column: Column.TARGET,
        key: ColumnKeys.TARGET,
        columnName: translateFn('Target'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<Target>(Target).map((target: Target): ColumnValue => ({
          label: this.stringsTranslator.translateEdrResponseActionsTarget(target),
          value: target,
        })),
        removable: false,
        filterResetValue: [],
        sortable: true,
      },
      {
        column: Column.DETAILS,
        key: ColumnKeys.DETAILS,
        columnName: translateFn('Details'),
        type: ColumnType.STRING,
        removable: false,
        filteringDisabled: true,
        sortable: false,
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<Status>(Status).map((status: Status): ColumnValue => ({
          label: this.stringsTranslator.translateEdrResponseActionsStatus(status),
          value: status,
        })),
        removable: false,
        filterResetValue: [],
        sortable: true,
      },
      {
        column: Column.UPDATE_STATUS_ACTIONS,
        key: ColumnKeys.UPDATE_STATUS_ACTIONS,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
      },
      {
        column: Column.EXECUTED_ON,
        key: ColumnKeys.EXECUTED_ON,
        columnName: translateFn('Executed on'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
      },
      {
        column: Column.EXECUTED_BY,
        key: ColumnKeys.EXECUTED_BY,
        columnName: translateFn('Executed by'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.SOURCE,
        key: ColumnKeys.SOURCE,
        columnName: translateFn('Source'),
        type: ColumnType.ENUM,
        values: EnumUtils.GetNumericValues<Source>(Source).map((source: Source): ColumnValue => ({
          label: this.stringsTranslator.translateEdrResponseActionsSource(source),
          value: source,
        })),
        removable: true,
        filterResetValue: [],
        sortable: true,
      },
    ];
  }

  private getAllUpdateStatusActions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Dismiss'),
        value: Status.DISMISSED.toString(),
      },
      {
        title: translateFn('Mark as done'),
        value: Status.DONE_EXTERNALLY.toString(),
      },
    ];
  }
}
